import { ShieldAlert } from 'lucide-react';
import { useMemo, useState } from 'react';

import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Datatable } from '@@/datatables';
import { BasicTableSettings, createPersistedStore } from '@@/datatables/types';
import { useTableState } from '@@/datatables/useTableState';
import { Alert } from '@@/Alert';

import { useIsVulnerabilityScanningEnabled } from '../queries/useIsVulnerabilityScanningEnabled';
import { useVulnerabilityReportTable } from '../queries/useVulnerabilityReportTable';
import { VulnerabilityDetailsModal } from '../VulnerabilityDetailsModal';

import { createColumns } from './columns';
import { fromTable } from './fromTable';
import { VulnerabilityRow } from './types';

const storageKey = 'kubernetes.vulnerabilities';

// No auto-refresh setting: trivy-operator rewrites reports on a 24h TTL, so
// polling would only add noise. The page header's reload covers the rest.
const settingsStore = createPersistedStore<BasicTableSettings>(storageKey, {
  id: 'severity',
  desc: false,
});

export function VulnerabilitiesDatatable() {
  const tableState = useTableState(settingsStore, storageKey);
  const environmentId = useEnvironmentId();

  const [selected, setSelected] = useState<VulnerabilityRow | null>(null);

  const scanningEnabledQuery = useIsVulnerabilityScanningEnabled(environmentId);
  const isScanningEnabled = scanningEnabledQuery.data ?? false;

  const reportsQuery = useVulnerabilityReportTable({
    environmentId,
    enabled: isScanningEnabled,
    select: fromTable,
  });

  const columns = useMemo(() => createColumns(setSelected), []);
  const rows = reportsQuery.data ?? [];

  if (scanningEnabledQuery.isSuccess && !isScanningEnabled) {
    return (
      <Alert color="info" title="Vulnerability scanning is not enabled">
        No vulnerability scanning policy is applied to this environment, so
        nothing is scanning its images. Attach a vulnerability scanning policy
        to one of its environment groups to start collecting findings.
      </Alert>
    );
  }

  if (reportsQuery.isError) {
    return (
      <Alert color="error" title="Unable to read vulnerability reports">
        This environment has no scanner reports, or you do not have access to
        them. Vulnerability scanning must be enabled for this environment, and
        reading reports requires access to aquasecurity.github.io resources.
      </Alert>
    );
  }

  return (
    <>
      <Datatable<VulnerabilityRow>
        dataset={rows}
        columns={columns}
        settingsManager={tableState}
        isLoading={scanningEnabledQuery.isLoading || reportsQuery.isLoading}
        title="Image vulnerabilities"
        titleIcon={ShieldAlert}
        description="Findings reported by the cluster's image scanner, one row per scanned container."
        getRowId={(row) => row.id}
        disableSelect
        emptyContentLabel="No vulnerability reports found"
        data-cy="k8s-vulnerabilities-datatable"
      />

      {selected && (
        <VulnerabilityDetailsModal
          environmentId={environmentId}
          namespace={selected.namespace}
          resourceName={selected.workload}
          onDismiss={() => setSelected(null)}
        />
      )}
    </>
  );
}
